import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader, StatusBadge, EmptyState } from '@/components/Shared';
import { money, shortDate } from '@/lib/format';
import { Percent, Clock, CheckCircle2, Wallet, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { DangerConfirm } from '@/components/DangerConfirm';

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-5"><div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">{label}</span><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}><Icon className="h-[18px] w-[18px]" /></span></div><div className="mt-2 text-2xl font-semibold tabular-nums font-heading">{value}</div></Card>
  );
}

export default function Commissions() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const load = async () => { const { data } = await api.get('/commissions'); setData(data); };
  useEffect(() => { load(); }, []);

  const payout = async (iid) => { try { await api.post(`/commissions/${iid}/pay`); toast.success('Commission marked paid'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const [delTarget, setDelTarget] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const doDeleteCommission = async (password) => {
    setDelBusy(true);
    try {
      await api.post(`/invoices/${delTarget.invoice_id}/commission/delete`, { password, confirm: 'CONFIRM' });
      toast.success('Commission record deleted');
      setDelTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setDelBusy(false); }
  };

  if (!data) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  return (
    <div>
      <PageHeader title={isAdmin ? 'Commissions' : 'My Commissions'} subtitle="Set manually per invoice — earned when the invoice is fully paid" testid="commissions-header" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <Stat icon={Clock} label="Pending" value={money(data.summary.pending)} accent="bg-slate-100 text-slate-700" />
        <Stat icon={CheckCircle2} label="Earned" value={money(data.summary.earned)} accent="bg-teal-50 text-teal-700" />
        <Stat icon={Wallet} label="Paid Out" value={money(data.summary.paid)} accent="bg-emerald-50 text-emerald-700" />
        <Stat icon={RotateCcw} label="Adjustments" value={money(data.summary.adjustments || 0)} accent="bg-red-50 text-red-700" />
      </div>
      <Card className="p-0 overflow-hidden">
        {data.rows.length === 0 ? <div className="p-6"><EmptyState icon={Percent} title="No commissions yet" description="Commissions appear once invoices are created." /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Invoice</TableHead>{isAdmin && <TableHead>Employee</TableHead>}<TableHead>Customer</TableHead><TableHead className="text-right">Sale</TableHead><TableHead className="text-right">Commission</TableHead><TableHead>Payment</TableHead><TableHead>Commission</TableHead>{isAdmin && <TableHead></TableHead>}</TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.invoice_id} data-testid="commission-row">
                    <TableCell className="font-medium">{r.invoice_number}</TableCell>
                    {isAdmin && <TableCell>{r.employee_name}</TableCell>}
                    <TableCell>{r.customer_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.sale_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(r.commission_amount)}</TableCell>
                    <TableCell><StatusBadge kind="payment" value={r.payment_status} /></TableCell>
                    <TableCell><StatusBadge kind="commission" value={r.commission_status} /></TableCell>
                    {isAdmin && <TableCell>
                      <div className="flex gap-1 justify-end">
                        {r.commission_status === 'earned' && <Button size="sm" variant="outline" onClick={() => payout(r.invoice_id)} data-testid="commission-payout-button">Pay Out</Button>}
                        {Math.abs(r.commission_amount || 0) > 0.005 && <Button size="sm" variant="ghost" onClick={() => setDelTarget(r)} data-testid="commission-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                      </div>
                    </TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <DangerConfirm
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title="Delete commission record?"
        description="Sets the commission on this invoice to $0. The invoice itself is kept. This cannot be undone."
        affectedText={delTarget ? `${delTarget.invoice_number} · ${delTarget.employee_name || '—'} · ${money(delTarget.commission_amount)}` : ''}
        onConfirm={doDeleteCommission}
        busy={delBusy}
        confirmLabel="Delete Commission"
      />
    </div>
  );
}
